import {
  QuoteCancelationPostRequestBody,
  QuoteCancelationPostRequestParams,
  QuoteCancelationPostResponse,
  QuoteSearchResponse,
} from '@lio/api-types';
import { QuoteStatusId } from '@models/quotes';
import { useQueryClient, useMutation, InfiniteData } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';

export const useCancelQuote = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const mutationFn = ({
    quoteId,
    reason,
    description,
    details,
  }: QuoteCancelationPostRequestBody & QuoteCancelationPostRequestParams) => {
    return client
      .post(`quote/${quoteId}/cancelation`, {
        json: { reason, description, details },
      })
      .json<QuoteCancelationPostResponse>();
  };
  const result = useMutation({
    mutationFn,
    onMutate: async (updatedData) => {
      const queryKey = ['quotes'];
      await queryClient.cancelQueries({ queryKey });
      const previousQuotes = queryClient.getQueriesData<InfiniteData<QuoteSearchResponse>>({ queryKey });
      queryClient.setQueriesData<InfiniteData<QuoteSearchResponse>>(
        { queryKey },
        (previousState) =>
          ({
            ...previousState,

            pages: previousState?.pages?.map((page) => ({
              ...page,
              items: page.items?.map((q) => {
                if (q.id === updatedData.quoteId) {
                  return { ...q, statusId: QuoteStatusId.Canceled };
                }
                return q;
              }),
            })),
          }) as InfiniteData<QuoteSearchResponse>,
      );
      return { previousQuotes };
    },
    onSuccess: async ({ quoteId }) => {
      const queryKey = ['quote', { quoteId }];
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: ['quotes'] });
      await queryClient.invalidateQueries({ queryKey: ['quoteCancelation'] });
    },
  });
  return { ...result, cancelQuote: result.mutateAsync };
};
